/**
 * Filtering primitives shared by the event and product finders.
 *
 * Both tools answer the same two questions about a piece of content — "is this in
 * the place the visitor named?" and "does this match what they typed?" — over
 * collections that carry the same imported `address` field and the same
 * free-text shape. Keeping the answers here means a town spelled "Kragerø" or
 * "kragero" resolves identically whichever tool the assistant reached for, and
 * that the district vocabulary is defined once.
 *
 * Like the finder cores, this file is deliberately free of imports so it stays
 * unit-testable without a CMS or a Redis instance.
 */

/**
 * Fold Norwegian/Swedish characters so "Kragerø" and "kragero" compare equal —
 * visitors type both, and so does the model.
 */
export function normalize(value) {
  return (
    String(value ?? '')
      .toLowerCase()
      // WP term names arrive HTML-encoded ("Aktiv &amp; friluft").
      .replace(/&amp;/g, '&')
      .replace(/&nbsp;/g, ' ')
      .replace(/[øö]/g, 'o')
      .replace(/[æä]/g, 'a')
      .replace(/å/g, 'a')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

// Districts a visitor names as if they were places. Telemark itself covers the
// whole site, so it resolves to "no filter" rather than to nothing. Grenland is
// the established district name for the towns around the Skien/Porsgrunn fjord.
// EDITORIAL: worth reviewing with the Visit Telemark team if more district names
// start showing up in the chat logs.
export const REGIONS = {
  telemark: null,
  'vestfold og telemark': null,
  'vestfold-og-telemark': null,
  grenland: ['skien', 'porsgrunn', 'brevik', 'langesund', 'stathelle', 'siljan', 'bamble', 'herre']
};

/**
 * The town an event takes place in, derived from the trailing "postcode town" of
 * the imported address (e.g. "Lundegt. 6, Ibsenhuset, 3724 Skien" → "Skien").
 * Parses cleanly for ~96% of events; the rest fall back to plain address matching.
 */
export function cityOf(event) {
  const match = String(event?.address ?? '')
    .trim()
    .match(/\b\d{4}\s+([^,]+)\s*$/u);
  return match ? match[1].trim() : '';
}

/**
 * Resolve the requested place to a matcher.
 *
 * @returns {{ kind: 'all'|'district'|'place', label: string, test: (event) => boolean }}
 */
export function placeMatcher(place) {
  const wanted = normalize(place);

  if (Object.prototype.hasOwnProperty.call(REGIONS, wanted)) {
    const towns = REGIONS[wanted];
    // Telemark → the whole site; no filtering, but say so in the response.
    if (!towns) return { kind: 'all', label: place, test: () => true };
    return {
      kind: 'district',
      label: place,
      test: (event) => towns.includes(normalize(cityOf(event)))
    };
  }

  return {
    kind: 'place',
    label: place,
    test: (event) => {
      const city = normalize(cityOf(event));
      if (city === wanted || city.startsWith(wanted)) return true;
      // Venue and district names ("Gaustatoppen", "Brekkeparken") live inside the
      // address rather than in the town field.
      return normalize(event?.address).includes(wanted);
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Dates                                                                       */
/* -------------------------------------------------------------------------- */

// The site's timezone — "today" must be the local date in Telemark, not UTC, or
// the invariant drifts by a day for a couple of hours every night.
const SITE_TIMEZONE = process.env.CHATBOT_TIMEZONE || 'Europe/Oslo';

/** Today's date in the site's timezone, as YYYY-MM-DD. */
export function today() {
  // sv-SE formats as ISO (YYYY-MM-DD), which is why it is used here rather than
  // the site language — this string is never shown to a visitor.
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: SITE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
}

/** True for a well-formed YYYY-MM-DD string. */
export function isIsoDate(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** ISO weekday, Monday = 1 … Sunday = 7. */
export function weekdayOf(iso) {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/* -------------------------------------------------------------------------- */
/* Text                                                                        */
/* -------------------------------------------------------------------------- */

export function truncate(text, max) {
  const value = String(text ?? '').trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max).replace(/\s+\S*$/, '')}…`;
}

/**
 * Free-text scoring across title, excerpt and address: how many of the query's
 * terms the item carries. Callers use it as a filter rather than as the primary
 * ranking signal — events stay in date order, products in relevance order.
 */
export function textScore(event, terms) {
  if (!terms.length) return 1;
  const haystack = normalize(`${event.title ?? ''} ${event.excerpt ?? ''} ${event.address ?? ''}`);
  return terms.filter((term) => haystack.includes(term)).length;
}

export function queryTerms(query) {
  return normalize(query)
    .split(' ')
    .filter((term) => term.length >= 3);
}
